/**
 * Image Comparison Component
 * 实现图片前后对比的拖拽功能
 */

class ImageComparison extends HTMLElement {
  constructor() {
    super();
    this.container = null;
    this.divider = null;
    this.afterImage = null;
    this.isDragging = false;
    this.currentPosition = 50; // 初始位置在中间 (百分比)
  }

  connectedCallback() {
    this.container = this.querySelector('.image-comparison__container');
    this.divider = this.querySelector('.image-comparison__divider');
    this.afterImage = this.querySelector('.image-comparison__after');
    this.beforeLabel = this.querySelector('.image-comparison__label--before');
    this.afterLabel = this.querySelector('.image-comparison__label--after');

    if (!this.container || !this.divider || !this.afterImage) {
      console.error('Image comparison: Required elements not found');
      return;
    }

    this.init();
  }

  disconnectedCallback() {
    this.removeEventListeners();
  }

  init() {
    // 设置初始位置
    this.updatePosition(50);

    // 绑定事件
    this.bindEvents();
  }

  bindEvents() {
    // 鼠标事件
    this.divider.addEventListener('mousedown', this.handleStart.bind(this));
    document.addEventListener('mousemove', this.handleMove.bind(this));
    document.addEventListener('mouseup', this.handleEnd.bind(this));

    // 触摸事件
    this.divider.addEventListener('touchstart', this.handleStart.bind(this), { passive: true });
    document.addEventListener('touchmove', this.handleMove.bind(this), { passive: false });
    document.addEventListener('touchend', this.handleEnd.bind(this));

    // 容器点击事件 - 点击容器任意位置跳转到该位置
    this.container.addEventListener('click', this.handleContainerClick.bind(this));

    // 防止图片被拖拽
    const images = this.querySelectorAll('.image-comparison__image');
    images.forEach(img => {
      img.addEventListener('dragstart', (e) => e.preventDefault());
    });
  }

  removeEventListeners() {
    document.removeEventListener('mousemove', this.handleMove.bind(this));
    document.removeEventListener('mouseup', this.handleEnd.bind(this));
    document.removeEventListener('touchmove', this.handleMove.bind(this));
    document.removeEventListener('touchend', this.handleEnd.bind(this));
  }

  handleStart(e) {
    e.preventDefault();
    this.isDragging = true;
    this.container.style.cursor = 'ew-resize';
    
    // 添加拖拽状态类
    this.container.classList.add('is-dragging');
  }

  handleMove(e) {
    if (!this.isDragging) return;

    e.preventDefault();

    // 获取容器位置和尺寸
    const containerRect = this.container.getBoundingClientRect();
    
    // 获取鼠标或触摸位置
    const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
    
    // 计算相对位置
    let position = ((clientX - containerRect.left) / containerRect.width) * 100;
    
    // 限制范围在 0-100 之间
    position = Math.max(0, Math.min(100, position));
    
    this.updatePosition(position);
  }

  handleEnd(e) {
    if (!this.isDragging) return;
    
    this.isDragging = false;
    this.container.style.cursor = '';
    
    // 移除拖拽状态类
    this.container.classList.remove('is-dragging');
  }

  handleContainerClick(e) {
    // 如果点击的是滑块，不处理
    if (e.target.closest('.image-comparison__divider')) return;
    
    // 获取容器位置和尺寸
    const containerRect = this.container.getBoundingClientRect();
    
    // 计算点击位置
    let position = ((e.clientX - containerRect.left) / containerRect.width) * 100;
    
    // 限制范围在 0-100 之间
    position = Math.max(0, Math.min(100, position));
    
    // 平滑过渡到新位置
    this.animateToPosition(position);
  }

  updatePosition(position) {
    this.currentPosition = position;
    
    // 更新 after 图片的裁剪
    this.afterImage.style.clipPath = `inset(0 0 0 ${position}%)`;
    
    // 更新分割线位置
    this.divider.style.left = `${position}%`;

    // 更新分割线和手柄的透明度（接近边缘时渐隐）
    this.updateDividerOpacity(position);

    // 更新标签可见性
    this.updateLabelsVisibility(position);
  }

  updateDividerOpacity(position) {
    // 在接近边缘时让分割线和手柄渐隐，避免遮挡图片
    let opacity = 1;
    
    // 左边缘渐隐：0-2% 范围内
    if (position < 2) {
      opacity = position / 2; // 从 0 到 1 线性渐变
    }
    // 右边缘渐隐：98-100% 范围内
    else if (position > 98) {
      opacity = (100 - position) / 2; // 从 1 到 0 线性渐变
    }
    
    this.divider.style.opacity = opacity.toString();
  }

  updateLabelsVisibility(position) {
    // Before 标签(左侧)：当 before 图片可见时显示（position > 标签位置），被 after 图片覆盖时隐藏
    // After 标签(右侧)：当 after 图片可见时显示（position < 标签位置），被裁剪掉时隐藏
    
    if (this.beforeLabel) {
      // 计算 before 标签的右边缘位置（百分比）
      // 标签在左侧 40px，假设标签宽度约 150px，总宽度 1196px
      // 右边缘大约在 (40 + 150) / 1196 ≈ 16%
      const beforeLabelRightEdge = 16;
      
      // 当 position > 标签位置时，before 图片在这个区域可见，显示标签
      // 当 position <= 标签位置时，before 图片被 after 图片覆盖，隐藏标签
      if (position > beforeLabelRightEdge) {
        this.beforeLabel.style.opacity = '1';
        this.beforeLabel.style.visibility = 'visible';
      } else {
        this.beforeLabel.style.opacity = '0';
        this.beforeLabel.style.visibility = 'hidden';
      }
    }

    if (this.afterLabel) {
      // 计算 after 标签的左边缘位置（百分比）
      // 标签在右侧 40px，假设标签宽度约 150px，总宽度 1196px
      // 左边缘大约在 (1196 - 40 - 150) / 1196 ≈ 84%
      const afterLabelLeftEdge = 84;
      
      // 当 position < 标签位置时，after 图片在这个区域可见，显示标签
      // 当 position >= 标签位置时，after 图片被裁剪掉，隐藏标签
      if (position < afterLabelLeftEdge) {
        this.afterLabel.style.opacity = '1';
        this.afterLabel.style.visibility = 'visible';
      } else {
        this.afterLabel.style.opacity = '0';
        this.afterLabel.style.visibility = 'hidden';
      }
    }
  }

  animateToPosition(targetPosition) {
    const startPosition = this.currentPosition;
    const difference = targetPosition - startPosition;
    const duration = 300; // 动画持续时间(ms)
    const startTime = performance.now();

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // 使用 easeOutCubic 缓动函数
      const easeProgress = 1 - Math.pow(1 - progress, 3);
      
      const newPosition = startPosition + (difference * easeProgress);
      this.updatePosition(newPosition);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }
}

// 注册自定义元素
if (!customElements.get('image-comparison')) {
  customElements.define('image-comparison', ImageComparison);
}
